// The component library: one record per component, read by /components, the
// search index and /mds/components.json.

export type ComponentDoc = {
  id: string
  name: string
  category: 'Identity' | 'Actions' | 'Forms' | 'Content' | 'Feedback' | 'Navigation' | 'Layout'
  summary: string
  useWhen: string[]
  anatomy: string[]
  accessibility: string[]
  /** USWDS components this replaces. */
  uswds: string[]
  /** Class names and tokens the implementation uses. */
  classes: string[]
  tokens: string[]
  /** Markup that reproduces the default variant. */
  example: string
  related: string[]
}

export const componentCategories: ComponentDoc['category'][] = [
  'Identity',
  'Navigation',
  'Actions',
  'Forms',
  'Content',
  'Feedback',
  'Layout',
]

export const components: ComponentDoc[] = [
  {
    id: 'banner',
    name: 'Banner',
    category: 'Identity',
    summary:
      'A line above the header that tells people whose site they are on. It explains itself in a dialog or in an expanding "Here\'s how you know" panel, centered or left-aligned.',
    useWhen: [
      'Every page of a government website, as the first element after the skip link.',
      'Use the official wording only on a .gov or .mil domain served over HTTPS.',
      'Use the alternate wording on documentation and pre-launch environments.',
    ],
    anatomy: ['Flag mark', 'Notice text', 'Information button or "Here\'s how you know" toggle', 'Explanation dialog or inline panel'],
    accessibility: [
      'In dialog mode the notice is a button with aria-haspopup="dialog"; in expand mode the toggle carries aria-expanded and aria-controls for the panel.',
      'The region is labelled "U.S. government websites" so screen reader users can skip it.',
      'Text stays at 12px minimum and meets 4.5:1 with ink-muted on bg.',
    ],
    uswds: ['Banner'],
    classes: ['mds-banner', 'mds-banner--center', 'mds-banner--left', 'mds-banner__button', 'mds-banner__toggle', 'mds-banner__panel', 'mds-banner-notice'],
    tokens: ['--mds-ink-muted', '--mds-text-micro', '--mds-radius-control'],
    example: `<section class="mds-banner" aria-label="U.S. government websites">
  <button type="button" class="mds-banner__button" aria-haspopup="dialog">
    <span class="mds-flag mds-flag--official"><!-- flag svg --></span>
    <span>An official website of the United States government</span>
  </button>
</section>`,
    related: ['header', 'identifier', 'dialog-sheet'],
  },
  {
    id: 'header',
    name: 'Header',
    category: 'Identity',
    summary:
      'A sticky, translucent bar with the mark and serif wordmark, primary links on wide screens and a Menu pill everywhere.',
    useWhen: [
      'At the top of every page, directly under the banner.',
      'Keep primary links to six or fewer; everything else goes in the Menu sheet.',
    ],
    anatomy: ['Lockup (mark and wordmark)', 'Primary navigation', 'Mission chip', 'Theme toggle', 'Menu button and sheet'],
    accessibility: [
      'The lockup is a single link named for the site home.',
      'Primary navigation is a nav landmark labelled "Main"; the current page is marked by the router.',
      'The Menu sheet is a native modal dialog, so focus is trapped and returns to the Menu button on close.',
    ],
    uswds: ['Header', 'Language selector'],
    classes: ['mds-header', 'mds-lockup', 'mds-header__nav', 'mds-header__menu', 'mds-menu'],
    tokens: ['--mds-bg', '--mds-font-display', '--mds-header-height', '--mds-primary'],
    example: `<header class="mds-header">
  <div class="mds-container mds-header__inner">
    <a class="mds-header__home" href="/">
      <span class="mds-lockup"><span class="mds-lockup__word">Agency name</span></span>
    </a>
    <nav class="mds-header__nav" aria-label="Main">…</nav>
    <button class="mds-button mds-button--primary mds-button--sm" aria-haspopup="dialog">Menu</button>
  </div>
</header>`,
    related: ['banner', 'theme-toggle', 'mission-picker', 'dialog-sheet'],
  },
  {
    id: 'footer',
    name: 'Footer',
    category: 'Identity',
    summary:
      'Closes every page in one of four layouts drawn from federal sites: editorial, columns, dark and studio, with an optional agency identifier.',
    useWhen: [
      'Editorial for a centered, quiet close; columns when a site has many sections; dark for a strong ending; studio for a single-purpose site.',
      'Turn on the identifier when the site belongs to an agency.',
    ],
    anatomy: ['Lockup', 'Navigation links or link columns', 'Social links', 'Optional identifier', 'Site bar: policy links and attribution'],
    accessibility: [
      'Each link group is a nav landmark with its own label: Footer, Policies.',
      'Column headings are real headings, so screen reader users can jump between groups.',
      'Links underline on hover and show the focus ring on keyboard focus.',
    ],
    uswds: ['Footer'],
    classes: ['mds-site-footer', 'mds-footer', 'mds-footer--editorial', 'mds-footer--columns', 'mds-footer--dark', 'mds-footer--studio', 'mds-footer__links', 'mds-footer__columns', 'mds-footer__social', 'mds-site-bar'],
    tokens: ['--mds-border', '--mds-ink-muted', '--mds-surface'],
    example: `<footer class="mds-site-footer">
  <div class="mds-footer mds-footer--editorial">
    <div class="mds-container mds-footer__editorial">
      <a href="/">Agency name</a>
      <nav class="mds-footer__links" aria-label="Footer">
        <a href="/services">Services</a>
        <a href="/about">About</a>
      </nav>
      <ul class="mds-footer__social" aria-label="Agency on social media">…</ul>
    </div>
  </div>
  <div class="mds-site-bar">
    <div class="mds-container mds-site-bar__inner">…</div>
  </div>
</footer>`,
    related: ['identifier', 'header'],
  },
  {
    id: 'identifier',
    name: 'Identifier',
    category: 'Identity',
    summary:
      'Names the parent agency and carries the links every federal site is required to provide.',
    useWhen: [
      'On agency sites, inside the footer.',
      'Point each required link to the agency page that fulfills it.',
    ],
    anatomy: ['Domain and agency masthead', 'Required links', 'USA.gov referral'],
    accessibility: [
      'Labelled region "Agency identifier" with a nav labelled "Important links".',
      'Links meet 4.5:1 using the link role on surface.',
    ],
    uswds: ['Identifier'],
    classes: ['mds-identifier', 'mds-identifier__links', 'mds-identifier__usa'],
    tokens: ['--mds-surface', '--mds-link', '--mds-radius-card'],
    example: `<section class="mds-identifier" aria-label="Agency identifier">
  <p class="mds-identifier__masthead">
    <span class="mds-identifier__domain">agency.gov</span>
    An official website of the Agency
  </p>
  <nav class="mds-identifier__links" aria-label="Important links">
    <a href="/about">About the agency</a>
    <a href="/accessibility">Accessibility statement</a>
    <a href="/foia">FOIA requests</a>
  </nav>
</section>`,
    related: ['footer', 'banner'],
  },
  {
    id: 'button',
    name: 'Button',
    category: 'Actions',
    summary: 'Pill-shaped actions in primary, secondary, accent, ghost and glass variants, at three sizes.',
    useWhen: [
      'Primary for the single most important action in a view.',
      'Secondary for alternatives; ghost for low-emphasis actions in dense areas.',
      'Accent sparingly, for a signature call to action.',
      'Use a link styled as a button only when the action navigates.',
    ],
    anatomy: ['Container', 'Label', 'Optional icon'],
    accessibility: [
      'Minimum 44px target height at the default size.',
      'Label text meets 4.5:1 on its fill in light and dark.',
      'Disabled buttons keep their label readable and use aria-disabled when they must stay focusable.',
    ],
    uswds: ['Button', 'Button group'],
    classes: ['mds-button', 'mds-button--primary', 'mds-button--secondary', 'mds-button--accent', 'mds-button--ghost', 'mds-button--glass', 'mds-button--sm', 'mds-button--lg'],
    tokens: ['--mds-primary', '--mds-on-primary', '--mds-primary-hover', '--mds-radius-control', '--mds-density'],
    example: `<button type="button" class="mds-button mds-button--primary">Continue</button>
<button type="button" class="mds-button mds-button--secondary">Save for later</button>`,
    related: ['segmented-control', 'prompt-pill'],
  },
  {
    id: 'prompt-pill',
    name: 'Prompt pill',
    category: 'Forms',
    summary: 'A large rounded field with an attached circular action, the signature entry point of a home page.',
    useWhen: [
      'The one question a page exists to answer: search, ask, or choose.',
      'Once per page, usually inside the hero.',
    ],
    anatomy: ['Container', 'Input or trigger text', 'Send button'],
    accessibility: [
      'The input has a visible or programmatic label; placeholder text is never the only label.',
      'The send button has an accessible name such as "Search".',
      'Focus moves the ring to the whole pill so the field reads as one control.',
    ],
    uswds: ['Search'],
    classes: ['mds-prompt', 'mds-prompt__input', 'mds-prompt__send'],
    tokens: ['--mds-raised', '--mds-shadow-field', '--mds-primary', '--mds-focus'],
    example: `<form class="mds-prompt" role="search">
  <label class="mds-visually-hidden" for="ask">Ask a question</label>
  <input id="ask" class="mds-prompt__input" placeholder="Try ‘renew my passport’" />
  <button class="mds-prompt__send" aria-label="Search">→</button>
</form>`,
    related: ['search', 'button'],
  },
  {
    id: 'search',
    name: 'Search',
    category: 'Forms',
    summary: 'A compact search field with a leading icon and an attached button, for headers and listings.',
    useWhen: ['Searching a site or filtering a list.', 'Use the prompt pill instead for a hero-level question.'],
    anatomy: ['Search landmark', 'Label', 'Icon', 'Input', 'Submit button'],
    accessibility: [
      'Wrap in role="search" with a label.',
      'Announce result counts in a polite live region after filtering.',
    ],
    uswds: ['Search'],
    classes: ['mds-search', 'mds-search__icon', 'mds-search__input'],
    tokens: ['--mds-raised', '--mds-border-strong', '--mds-radius-control'],
    example: `<form class="mds-search" role="search">
  <label class="mds-visually-hidden" for="q">Search</label>
  <input id="q" class="mds-search__input" type="search" placeholder="Search" />
  <button class="mds-button mds-button--primary mds-button--sm">Search</button>
</form>`,
    related: ['prompt-pill', 'text-input'],
  },
  {
    id: 'card',
    name: 'Card',
    category: 'Content',
    summary: 'A rounded surface that groups a title, supporting text and one action. Flat, outline, raised and link variants.',
    useWhen: [
      'Collections of comparable items: services, programs, articles.',
      'Use the link variant when the whole card goes to one place.',
    ],
    anatomy: ['Surface', 'Optional tag', 'Title', 'Body', 'Action'],
    accessibility: [
      'In the link variant one link carries the name; a stretched ::after makes the whole card clickable without nesting interactive elements.',
      'Headings inside cards follow the page outline.',
    ],
    uswds: ['Card', 'Collection'],
    classes: ['mds-card', 'mds-card--outline', 'mds-card--raised', 'mds-card--link', 'mds-card__title', 'mds-card__link'],
    tokens: ['--mds-surface', '--mds-raised', '--mds-radius-card', '--mds-shadow-1'],
    example: `<article class="mds-card mds-card--outline mds-card--link">
  <span class="mds-tag">Benefits</span>
  <h3 class="mds-card__title"><a class="mds-card__link" href="/apply">Apply for benefits</a></h3>
  <p>Check eligibility and apply online.</p>
</article>`,
    related: ['media-card', 'tag', 'stat'],
  },
  {
    id: 'media-card',
    name: 'Media card',
    category: 'Content',
    summary: 'A large rounded image or video with a caption and a play or action pill floating over it.',
    useWhen: ['Featured stories, campaign video and program photography.'],
    anatomy: ['Media', 'Scrim', 'Caption', 'Action pill'],
    accessibility: [
      'Images carry alt text unless decorative; video has captions and does not autoplay with sound.',
      'Overlaid text sits on a scrim so it meets 4.5:1 over any frame.',
    ],
    uswds: ['Card'],
    classes: ['mds-media-card', 'mds-media-card__media', 'mds-media-card__caption'],
    tokens: ['--mds-radius-hero', '--mds-shadow-2'],
    example: `<figure class="mds-media-card">
  <img class="mds-media-card__media" src="/media/hero-capitol-sm.jpg" alt="The west front of the U.S. Capitol." />
  <figcaption class="mds-media-card__caption">
    <span>The Capitol</span>
    <a class="mds-button mds-button--glass mds-button--sm" href="/story">Read the story</a>
  </figcaption>
</figure>`,
    related: ['card'],
  },
  {
    id: 'stat',
    name: 'Stat',
    category: 'Content',
    summary: 'A large figure in the display serif with a short label, for outcomes and service levels.',
    useWhen: ['Reporting a figure people care about: wait times, savings, people served.'],
    anatomy: ['Value', 'Label', 'Optional source'],
    accessibility: [
      'Animated count-ups expose the final value to assistive technology and stop under reduced motion.',
      'Units are written out in the label rather than conveyed by layout.',
    ],
    uswds: ['Summary box'],
    classes: ['mds-stat', 'mds-stat__value', 'mds-stat__label'],
    tokens: ['--mds-font-display', '--mds-ink-muted'],
    example: `<div class="mds-stat">
  <span class="mds-stat__value">4–6</span>
  <span class="mds-stat__label">weeks for routine renewal</span>
</div>`,
    related: ['card'],
  },
  {
    id: 'accordion',
    name: 'Accordion',
    category: 'Content',
    summary: 'Questions that expand to answers, with a plus that turns to a minus. Built on details and summary.',
    useWhen: ['Frequently asked questions and optional detail people scan for.', 'Never hide information everyone needs.'],
    anatomy: ['Item', 'Summary row', 'Plus icon', 'Body'],
    accessibility: [
      'Native details and summary give keyboard and screen reader support with no script.',
      'The summary text is the question; the icon is decorative.',
    ],
    uswds: ['Accordion'],
    classes: ['mds-accordion', 'mds-accordion__item', 'mds-accordion__summary', 'mds-accordion__icon', 'mds-accordion__body'],
    tokens: ['--mds-border', '--mds-ink-muted', '--mds-ease-out'],
    example: `<div class="mds-accordion">
  <details class="mds-accordion__item">
    <summary class="mds-accordion__summary">Who can apply?<span class="mds-accordion__icon" aria-hidden="true"></span></summary>
    <p class="mds-accordion__body">Any U.S. citizen with a valid passport.</p>
  </details>
</div>`,
    related: ['card'],
  },
  {
    id: 'alert',
    name: 'Alert',
    category: 'Feedback',
    summary: 'A tinted message with an icon in info, success, warning and error tones.',
    useWhen: ['Status that affects the whole page or task.', 'Use field errors, not an alert, for a single input.'],
    anatomy: ['Container', 'Icon', 'Title', 'Body'],
    accessibility: [
      'Messages that appear after an action use role="status" or role="alert" by urgency.',
      'Tone is carried by the title and icon as well as color.',
    ],
    uswds: ['Alert', 'Site alert'],
    classes: ['mds-alert', 'mds-alert--success', 'mds-alert--warning', 'mds-alert--error', 'mds-alert__icon', 'mds-alert__title'],
    tokens: ['--mds-info', '--mds-success', '--mds-warning', '--mds-error', '--mds-raised'],
    example: `<div class="mds-alert mds-alert--success" role="status">
  <svg class="mds-alert__icon" aria-hidden="true">…</svg>
  <div>
    <p class="mds-alert__title">Application received</p>
    <p>We sent a confirmation to your email.</p>
  </div>
</div>`,
    related: ['text-input'],
  },
  {
    id: 'text-input',
    name: 'Text input',
    category: 'Forms',
    summary: 'A labelled field with hint and error text, sized for touch and readable in both themes.',
    useWhen: ['Short free-text answers.', 'Use a textarea for more than one line.'],
    anatomy: ['Label', 'Hint', 'Input', 'Error message'],
    accessibility: [
      'Hint and error are connected with aria-describedby; errors set aria-invalid.',
      'Control borders meet 3:1 against every surface.',
      'Validate on blur or submit, never on each keystroke.',
    ],
    uswds: ['Text input', 'Character count', 'Input mask', 'Input prefix or suffix', 'Validation', 'Form'],
    classes: ['mds-field', 'mds-label', 'mds-hint', 'mds-input', 'mds-error-text', 'mds-textarea'],
    tokens: ['--mds-border-strong', '--mds-error', '--mds-radius-field', '--mds-focus'],
    example: `<div class="mds-field">
  <label class="mds-label" for="zip">ZIP code</label>
  <span class="mds-hint" id="zip-hint">Five digits</span>
  <input id="zip" class="mds-input" inputmode="numeric" aria-describedby="zip-hint" />
</div>`,
    related: ['select', 'checkbox-radio', 'alert', 'textarea'],
  },
  {
    id: 'select',
    name: 'Select',
    category: 'Forms',
    summary: 'A native select styled to match the text input, with a drawn chevron.',
    useWhen: ['Choosing one of more than six known options.', 'Use radio buttons for six or fewer.'],
    anatomy: ['Label', 'Hint', 'Select', 'Chevron'],
    accessibility: ['The native element keeps platform keyboard and screen reader behavior.'],
    uswds: ['Select', 'Combo box', 'Date picker', 'Memorable date', 'Time picker'],
    classes: ['mds-field', 'mds-label', 'mds-select'],
    tokens: ['--mds-border-strong', '--mds-radius-field'],
    example: `<div class="mds-field">
  <label class="mds-label" for="state">State</label>
  <select id="state" class="mds-select"><option>Virginia</option></select>
</div>`,
    related: ['text-input', 'checkbox-radio'],
  },
  {
    id: 'checkbox-radio',
    name: 'Checkbox and radio',
    category: 'Forms',
    summary: 'Native checkboxes and radio buttons tinted with the primary role, in large touch rows.',
    useWhen: ['Checkboxes for any number of choices; radios for exactly one of a few.'],
    anatomy: ['Fieldset and legend', 'Control', 'Label'],
    accessibility: [
      'Group related controls in a fieldset with a legend.',
      'The whole row is the label, so the target is the full line.',
    ],
    uswds: ['Checkbox', 'Radio buttons'],
    classes: ['mds-choice'],
    tokens: ['--mds-primary'],
    example: `<fieldset>
  <legend class="mds-label">Contact me by</legend>
  <label class="mds-choice"><input type="checkbox" /> Email</label>
  <label class="mds-choice"><input type="checkbox" /> Text message</label>
</fieldset>`,
    related: ['segmented-control', 'select'],
  },
  {
    id: 'tag',
    name: 'Tag',
    category: 'Content',
    summary: 'A small pill for categories and status, in tint, outline and accent styles.',
    useWhen: ['Labelling items with a category or state.', 'Pair status tags with words, never color alone.'],
    anatomy: ['Pill', 'Optional dot', 'Label'],
    accessibility: ['Tags are text, not controls; use a button when a tag removes a filter.'],
    uswds: ['Tag'],
    classes: ['mds-tag', 'mds-tag--accent', 'mds-tag--outline', 'mds-tag__dot'],
    tokens: ['--mds-primary', '--mds-accent', '--mds-on-accent', '--mds-radius-control'],
    example: `<span class="mds-tag">Benefits</span>
<span class="mds-tag mds-tag--accent">New</span>`,
    related: ['card'],
  },
  {
    id: 'dialog-sheet',
    name: 'Dialog and sheet',
    category: 'Layout',
    summary: 'Modal surfaces built on the native dialog element: a centered dialog and a sheet that slides from the edge.',
    useWhen: ['Dialog for a short decision or explanation.', 'Sheet for menus and secondary panels.'],
    anatomy: ['Backdrop', 'Surface', 'Title', 'Close button', 'Body'],
    accessibility: [
      'showModal() supplies the focus trap, inert background and Escape to close.',
      'aria-labelledby points at the visible title.',
      'Focus returns to the control that opened it.',
    ],
    uswds: ['Modal'],
    classes: ['mds-dialog', 'mds-sheet', 'mds-dialog__inner', 'mds-sheet__inner'],
    tokens: ['--mds-raised', '--mds-shadow-2', '--mds-ease-drawer', '--mds-duration-slow'],
    example: `<dialog class="mds-dialog" aria-labelledby="d-title">
  <div class="mds-dialog__inner">
    <div class="mds-dialog__head"><h2 id="d-title">About this site</h2><button aria-label="Close">×</button></div>
    <p>…</p>
  </div>
</dialog>`,
    related: ['header', 'banner', 'mission-picker'],
  },
  {
    id: 'theme-toggle',
    name: 'Theme toggle',
    category: 'Actions',
    summary: 'A single button that switches between light and dark. The site follows the system until someone chooses.',
    useWhen: ['In the header of any site that ships both themes.'],
    anatomy: ['Icon button', 'Sun or moon icon'],
    accessibility: [
      'Named "Dark theme" with aria-pressed reflecting the current state.',
      'A startup script applies the saved choice before first paint, so the page never flashes.',
    ],
    uswds: [],
    classes: ['mds-icon-button', 'mds-theme-toggle'],
    tokens: ['--mds-ink'],
    example: `<button type="button" class="mds-icon-button mds-theme-toggle" aria-label="Dark theme" aria-pressed="false">
  <svg aria-hidden="true">…</svg>
</button>`,
    related: ['header', 'segmented-control'],
  },
  {
    id: 'mission-picker',
    name: 'Mission picker',
    category: 'Navigation',
    summary: 'A searchable dialog of every mission theme, grouped by department and agency, that reskins the page on choice.',
    useWhen: ['Previewing MDS for an agency.', 'Any multi-agency site where people choose an organization.'],
    anatomy: ['Dialog', 'Combobox input', 'Grouped listbox', 'Option with mark, name and swatches'],
    accessibility: [
      'The input is a combobox controlling a listbox; arrow keys move aria-activedescendant and Enter chooses.',
      'Groups are labelled so screen readers announce the section.',
    ],
    uswds: ['Combo box'],
    classes: ['mds-mission-picker', 'mds-mission-list', 'mds-mission-option'],
    tokens: ['--mds-primary', '--mds-accent'],
    example: `<input class="mds-input" role="combobox" aria-expanded="true" aria-controls="missions" />
<div id="missions" role="listbox" class="mds-mission-list">
  <div role="option" aria-selected="false" class="mds-mission-option" data-mission="usda">…</div>
</div>`,
    related: ['dialog-sheet', 'header'],
  },
  {
    id: 'segmented-control',
    name: 'Segmented control',
    category: 'Actions',
    summary: 'A row of pressed-state buttons on a tinted track for switching views or settings.',
    useWhen: ['Two to five mutually exclusive views of the same content.'],
    anatomy: ['Track', 'Option buttons'],
    accessibility: ['Each option is a button with aria-pressed; the group has a label.'],
    uswds: ['Button group'],
    classes: ['mds-segmented', 'mds-segmented__option'],
    tokens: ['--mds-raised', '--mds-ink-muted', '--mds-radius-control'],
    example: `<div class="mds-segmented" role="group" aria-label="Period">
  <button class="mds-segmented__option" aria-pressed="true">Week</button>
  <button class="mds-segmented__option" aria-pressed="false">Month</button>
</div>`,
    related: ['button', 'checkbox-radio'],
  },
  {
    id: 'breadcrumb',
    name: 'Breadcrumb',
    category: 'Navigation',
    summary: 'A single line of ancestor links that ends at the current page.',
    useWhen: ['Pages two or more levels deep.'],
    anatomy: ['Nav landmark', 'Ancestor links', 'Separators', 'Current page'],
    accessibility: ['nav labelled "Breadcrumb"; the last item has aria-current="page".'],
    uswds: ['Breadcrumb'],
    classes: ['mds-breadcrumb'],
    tokens: ['--mds-ink-muted', '--mds-link'],
    example: `<nav class="mds-breadcrumb" aria-label="Breadcrumb">
  <ol>
    <li><a href="/">Home</a></li>
    <li><a href="/services">Services</a></li>
    <li aria-current="page">Passports</li>
  </ol>
</nav>`,
    related: ['header', 'step-indicator'],
  },
  {
    id: 'step-indicator',
    name: 'Step indicator',
    category: 'Navigation',
    summary: 'A segmented progress bar with the current step named, for multi-step forms.',
    useWhen: ['Forms of three to seven steps.'],
    anatomy: ['Segments', 'Step count', 'Current step heading'],
    accessibility: [
      'The list exposes each step with a visually hidden status: completed, current or not completed.',
      'The current step heading says "Step 2 of 4".',
    ],
    uswds: ['Step indicator', 'Process list'],
    classes: ['mds-steps', 'mds-steps__segment', 'mds-steps__heading'],
    tokens: ['--mds-primary', '--mds-border'],
    example: `<div class="mds-steps">
  <ol class="mds-steps__list">
    <li class="mds-steps__segment is-complete">Eligibility</li>
    <li class="mds-steps__segment is-current" aria-current="step">Details</li>
    <li class="mds-steps__segment">Review</li>
  </ol>
  <p class="mds-steps__heading">Step 2 of 3: Details</p>
</div>`,
    related: ['breadcrumb', 'text-input'],
  },
  {
    id: 'pagination',
    name: 'Pagination',
    category: 'Navigation',
    summary: 'Previous and next pills with page numbers between them.',
    useWhen: ['Long lists split into pages.', 'Prefer filtering when people search for one item.'],
    anatomy: ['Nav landmark', 'Previous', 'Page links', 'Ellipsis', 'Next'],
    accessibility: [
      'nav labelled "Pagination"; the current page has aria-current="page".',
      'Each page link is named "Page 3", not just "3".',
    ],
    uswds: ['Pagination'],
    classes: ['mds-pagination', 'mds-pagination__link'],
    tokens: ['--mds-primary', '--mds-on-primary', '--mds-radius-control'],
    example: `<nav class="mds-pagination" aria-label="Pagination">
  <a class="mds-pagination__link" href="?page=1">Previous</a>
  <a class="mds-pagination__link" href="?page=1" aria-label="Page 1">1</a>
  <a class="mds-pagination__link" aria-current="page" aria-label="Page 2">2</a>
  <a class="mds-pagination__link" href="?page=3">Next</a>
</nav>`,
    related: ['breadcrumb'],
  },
  {
    id: 'combo-box',
    name: 'Combo box',
    category: 'Forms',
    summary: 'A text field that narrows a long list as you type, so people find an option without scrolling a select.',
    useWhen: [
      'Choosing one option from a list longer than about fifteen items, such as a state, agency or country.',
      'Use a select for short lists and radio buttons for five or fewer options.',
    ],
    anatomy: ['Label', 'Hint', 'Text input with role="combobox"', 'Toggle button', 'Listbox of options'],
    accessibility: [
      'Follows the ARIA 1.2 combobox pattern: aria-expanded, aria-controls and aria-activedescendant on the input.',
      'Down and Up move through matches, Enter chooses, Escape closes the list and then clears the field.',
      'When nothing matches, "No matches" is announced in a status message.',
    ],
    uswds: ['Combo box'],
    classes: ['mds-combo', 'mds-combo__control', 'mds-combo__input', 'mds-combo__toggle', 'mds-combo__list', 'mds-combo__option'],
    tokens: ['--mds-raised', '--mds-primary', '--mds-radius-field', '--mds-shadow-1'],
    example: `import { ComboBox } from 'missiondesign-react'

<ComboBox label="State" hint="Start typing to filter" options={['Alabama', 'Alaska', 'Arizona']} />`,
    related: ['select', 'text-input', 'search'],
  },
  {
    id: 'date-picker',
    name: 'Date picker',
    category: 'Forms',
    summary: 'A date field people can type into, with a calendar for choosing a date near today.',
    useWhen: [
      'Dates close to the present, such as an appointment or a travel date.',
      'Use the memorable date for dates people know by heart, such as a date of birth.',
    ],
    anatomy: ['Label', 'Format hint', 'Text input', 'Calendar button', 'Calendar dialog with a grid of days'],
    accessibility: [
      'Typing MM/DD/YYYY always works, so the calendar is never the only way in.',
      'In the calendar, arrows move by day and week, Page Up and Page Down by month, Home and End to the edges of the week, Escape closes.',
      'Each day is named in full ("19 October 2026, Monday"); an invalid date is described by an error tied to the field.',
    ],
    uswds: ['Date picker'],
    classes: ['mds-date', 'mds-date__control', 'mds-date__toggle', 'mds-date__calendar', 'mds-date__grid', 'mds-date__day'],
    tokens: ['--mds-primary', '--mds-on-primary', '--mds-raised', '--mds-shadow-2'],
    example: `import { DatePicker } from 'missiondesign-react'

<DatePicker label="Appointment date" onChange={(date) => console.log(date)} />`,
    related: ['time-picker', 'memorable-date', 'text-input'],
  },
  {
    id: 'time-picker',
    name: 'Time picker',
    category: 'Forms',
    summary: 'A time field that offers times at a set step, filtered as you type, with a clock button for the full list.',
    useWhen: [
      'Appointment and pickup times, alongside a date picker.',
      'Set the earliest and latest times and the step to the times you can actually offer.',
    ],
    anatomy: ['Label', 'Format hint', 'Combo box input', 'Clock button', 'List of times', 'Optional time zone select'],
    accessibility: [
      'It is a combo box: typing filters the times, arrows move through them, Enter chooses, Escape closes and then clears.',
      'Times are written as people say them ("9:30 am"), and the clock button is named for the list it opens.',
      'The time zone is a labeled native select of U.S. zones, starting from the visitor\'s own.',
    ],
    uswds: ['Time picker'],
    classes: ['mds-time', 'mds-time--zone', 'mds-time__zone', 'mds-date-time', 'mds-combo', 'mds-combo__toggle', 'mds-combo__list', 'mds-combo__option'],
    tokens: ['--mds-raised', '--mds-primary', '--mds-radius-field', '--mds-shadow-1'],
    example: `import { DatePicker, TimePicker } from 'missiondesign-react'

<div className="mds-date-time">
  <DatePicker label="Appointment date" />
  <TimePicker label="Time" min={8 * 60} max={17 * 60} step={30} timeZone />
</div>`,
    related: ['date-picker', 'combo-box'],
  },
  {
    id: 'memorable-date',
    name: 'Memorable date',
    category: 'Forms',
    summary: 'Month, day and year as three fields, for dates people know without a calendar.',
    useWhen: ['Dates of birth, issue dates and other dates people remember or copy from a document.'],
    anatomy: ['Fieldset and legend', 'Month select', 'Day input', 'Year input', 'Hint'],
    accessibility: [
      'A fieldset and legend name the date; each part has its own visible label.',
      'Day and year use inputmode="numeric" and autocomplete="bday-day" and "bday-year" for dates of birth.',
    ],
    uswds: ['Memorable date'],
    classes: ['mds-memorable-date', 'mds-memorable-date__month', 'mds-memorable-date__day', 'mds-memorable-date__year'],
    tokens: ['--mds-radius-field', '--mds-border-strong'],
    example: `<fieldset class="mds-memorable-date">
  <legend class="mds-label">Date of birth</legend>
  <div class="mds-field mds-memorable-date__month">
    <label class="mds-label" for="dob-month">Month</label>
    <select class="mds-select" id="dob-month" autocomplete="bday-month">…</select>
  </div>
  <div class="mds-field mds-memorable-date__day">
    <label class="mds-label" for="dob-day">Day</label>
    <input class="mds-input" id="dob-day" inputmode="numeric" autocomplete="bday-day" />
  </div>
  <div class="mds-field mds-memorable-date__year">
    <label class="mds-label" for="dob-year">Year</label>
    <input class="mds-input" id="dob-year" inputmode="numeric" autocomplete="bday-year" />
  </div>
</fieldset>`,
    related: ['date-picker', 'text-input', 'select'],
  },
  {
    id: 'file-input',
    name: 'File input',
    category: 'Forms',
    summary: "A drop zone over the browser's own file picker, listing chosen files with a way to remove each.",
    useWhen: ['Uploading documents or photos as part of a form.', 'State the accepted types and size limit in the hint.'],
    anatomy: ['Label', 'Hint with types and size', 'Drop zone', 'File input', 'File list with remove buttons', 'Error'],
    accessibility: [
      "The real file input stays in the page, so the browser's picker works by keyboard and screen reader.",
      'Rejected files are named in an error announced as an alert; each remove button names its file.',
    ],
    uswds: ['File input'],
    classes: ['mds-file', 'mds-file__zone', 'mds-file__input', 'mds-file__list', 'mds-file__item'],
    tokens: ['--mds-border-strong', '--mds-primary', '--mds-error', '--mds-radius-card'],
    example: `import { FileInput } from 'missiondesign-react'

<FileInput label="Supporting documents" hint="PDF or JPG, up to 10 MB each" accept=".pdf,.jpg" maxMb={10} />`,
    related: ['text-input', 'alert'],
  },
  {
    id: 'character-count',
    name: 'Character count',
    category: 'Forms',
    summary: 'A text field that shows how many characters are left, and turns the count into an error past the limit.',
    useWhen: ['Fields with a firm length limit, such as a short description or a message to an agency.'],
    anatomy: ['Label', 'Hint', 'Text area or input', 'Remaining count'],
    accessibility: [
      'Typing past the limit is allowed, so nothing is cut off silently; the field becomes invalid instead.',
      'The count is tied to the field with aria-describedby and announced politely once typing pauses.',
    ],
    uswds: ['Character count'],
    classes: ['mds-count', 'mds-count__message', 'mds-textarea'],
    tokens: ['--mds-ink-muted', '--mds-error'],
    example: `import { CharacterCount } from 'missiondesign-react'

<CharacterCount label="Describe the issue" maxLength={250} />`,
    related: ['text-input'],
  },
  {
    id: 'data-table',
    name: 'Data table',
    category: 'Content',
    summary: 'A table that sorts, filters, selects and pages through rows, keeps its header in view, and stacks into labeled cards on phones.',
    useWhen: [
      'Comparing many records across the same fields, such as applications or facilities.',
      'Use cards or a list when each item is read on its own.',
    ],
    anatomy: ['Caption', 'Filter', 'Row count', 'Column headers with sort buttons', 'Select all and row checkboxes', 'Rows', 'Pagination'],
    accessibility: [
      'Sortable headers are buttons, and the sorted column carries aria-sort.',
      'The filter result, page range and selection count are announced in a status message.',
      'Pages are a navigation landmark named for the table; the current page has aria-current="page".',
      'On narrow screens each cell shows its column name, so the table never scrolls sideways at 320px.',
    ],
    uswds: ['Table'],
    classes: ['mds-table', 'mds-table--striped', 'mds-table--header-tinted', 'mds-table--header-solid', 'mds-table--header-bold', 'mds-table--header-underline', 'mds-table--stack', 'mds-table-scroll--sticky', 'mds-table__sort', 'mds-table__filter'],
    tokens: ['--mds-border', '--mds-surface', '--mds-primary', '--mds-density'],
    example: `import { DataTable } from 'missiondesign-react'

<DataTable
  caption="Field offices"
  rowKey="id"
  filter
  selectable
  stickyHeader
  pageSize={10}
  columns={[{ key: 'office', label: 'Office' }, { key: 'state', label: 'State' }, { key: 'wait', label: 'Wait (days)', numeric: true }]}
  rows={offices}
/>`,
    related: ['pagination', 'search'],
  },
  {
    id: 'side-navigation',
    name: 'Side navigation',
    category: 'Navigation',
    summary: 'A list of the pages in a section, with nested pages and the current page marked.',
    useWhen: ['Sections with more than a few pages, beside the content on wide screens.'],
    anatomy: ['Nav landmark', 'Links', 'Nested list', 'Current page'],
    accessibility: ['nav with its own label; the current page has aria-current="page".', 'Nesting uses real lists, so the depth is announced.'],
    uswds: ['Side navigation'],
    classes: ['mds-sidenav', 'mds-sidenav__link'],
    tokens: ['--mds-primary', '--mds-ink-muted', '--mds-radius-field'],
    example: `<nav aria-label="Benefits">
  <ul class="mds-sidenav">
    <li><a class="mds-sidenav__link" href="/benefits">Overview</a></li>
    <li>
      <a class="mds-sidenav__link" href="/benefits/apply" aria-current="page">How to apply</a>
      <ul>
        <li><a class="mds-sidenav__link" href="/benefits/apply/online">Online</a></li>
        <li><a class="mds-sidenav__link" href="/benefits/apply/mail">By mail</a></li>
      </ul>
    </li>
  </ul>
</nav>`,
    related: ['breadcrumb', 'header'],
  },
  {
    id: 'summary-box',
    name: 'Summary box',
    category: 'Content',
    summary: 'The key points of a page in a highlighted box near the top.',
    useWhen: ['Long pages where most people need the same two or three facts or next steps.'],
    anatomy: ['Region with heading', 'List of key points', 'Optional links'],
    accessibility: ['A labelled region with a real heading, so it can be found from the headings list.'],
    uswds: ['Summary box'],
    classes: ['mds-summary', 'mds-summary__title'],
    tokens: ['--mds-primary', '--mds-raised', '--mds-radius-card'],
    example: `<section class="mds-summary" aria-labelledby="summary-title">
  <h2 class="mds-summary__title" id="summary-title">Key information</h2>
  <ul>
    <li>Apply at least six weeks before you travel.</li>
    <li>Bring your current passport and one photo.</li>
  </ul>
</section>`,
    related: ['alert', 'card'],
  },
  {
    id: 'process-list',
    name: 'Process list',
    category: 'Content',
    summary: 'Numbered steps joined by a line, for a process people follow in order.',
    useWhen: ['Explaining how something works from start to finish, such as how to apply.', 'Use the step indicator for steps inside a form.'],
    anatomy: ['Ordered list', 'Step number', 'Step heading', 'Step details'],
    accessibility: ['An ordered list, so the count and position of each step are announced.'],
    uswds: ['Process list'],
    classes: ['mds-process', 'mds-process__item', 'mds-process__title'],
    tokens: ['--mds-primary', '--mds-on-primary', '--mds-border'],
    example: `<ol class="mds-process">
  <li class="mds-process__item">
    <h3 class="mds-process__title">Check eligibility</h3>
    <p>Answer five questions to see if you qualify.</p>
  </li>
  <li class="mds-process__item">
    <h3 class="mds-process__title">Apply online</h3>
    <p>Most applications take about twenty minutes.</p>
  </li>
</ol>`,
    related: ['step-indicator', 'accordion'],
  },
  {
    id: 'modal',
    name: 'Modal',
    category: 'Feedback',
    summary: 'A focused window for a decision or a short task, with its actions at the foot.',
    useWhen: [
      'Confirming something that cannot be undone, or a short task that must finish before going on.',
      'Use the forced variant only when a choice must be made; it has no close button.',
    ],
    anatomy: ['Backdrop', 'Title', 'Close button', 'Body', 'Actions'],
    accessibility: [
      'Built on the native dialog element: focus moves in and is held, the page behind is inert, and focus returns to the opener.',
      'Labelled by its title and described by its body; Escape closes unless the modal is forced.',
    ],
    uswds: ['Modal'],
    classes: ['mds-modal', 'mds-modal--lg', 'mds-modal__head', 'mds-modal__body', 'mds-modal__actions'],
    tokens: ['--mds-raised', '--mds-shadow-2', '--mds-radius-card'],
    example: `import { Modal } from 'missiondesign-react'

<Modal open={open} onClose={() => setOpen(false)} title="Withdraw your application?"
  actions={<button className="mds-button mds-button--primary">Withdraw</button>}>
  You can apply again later, but you will lose your place in line.
</Modal>`,
    related: ['dialog-sheet', 'button'],
  },
  {
    id: 'tooltip',
    name: 'Tooltip',
    category: 'Feedback',
    summary: 'A short description of a control, shown on hover and keyboard focus.',
    useWhen: ['Naming an icon-only control, or adding a brief detail that is not essential.', 'Never put instructions or errors in a tooltip.'],
    anatomy: ['Trigger', 'Tooltip text'],
    accessibility: [
      'Shown on hover and on keyboard focus; the trigger points to it with aria-describedby.',
      'It stays while the pointer is over it and closes with Escape (WCAG 1.4.13).',
    ],
    uswds: ['Tooltip'],
    classes: ['mds-tooltip', 'mds-tooltip--top', 'mds-tooltip--bottom', 'mds-tooltip__body'],
    tokens: ['--mds-ink', '--mds-bg'],
    example: `import { Tooltip } from 'missiondesign-react'

<Tooltip label="Download a PDF copy">
  <button className="mds-icon-button" aria-label="Download">…</button>
</Tooltip>`,
    related: ['button'],
  },
  {
    id: 'site-alert',
    name: 'Site alert',
    category: 'Feedback',
    summary: 'A slim notice across the top of every page, for information everyone visiting needs.',
    useWhen: ['Outages, deadlines and emergencies that affect the whole site.', 'Use the alert for messages about one page or task.'],
    anatomy: ['Region', 'Icon', 'Message', 'Optional link'],
    accessibility: [
      'A labelled region at the top of the page; emergency alerts use role="alert" only when they appear after load.',
      'Color is never the only signal: the icon and wording carry the meaning.',
    ],
    uswds: ['Site alert'],
    classes: ['mds-site-alert', 'mds-site-alert--emergency', 'mds-site-alert__link'],
    tokens: ['--mds-info', '--mds-error'],
    example: `<section class="mds-site-alert" aria-label="Site alert">
  <svg aria-hidden="true">…</svg>
  <p>Online services are unavailable Sunday from 1 to 5 a.m. Eastern.</p>
</section>`,
    related: ['alert', 'banner'],
  },
  {
    id: 'language-selector',
    name: 'Language selector',
    category: 'Navigation',
    summary: 'Switches the site language: one button for two languages, a menu for more.',
    useWhen: ['Sites published in more than one language, in the header.'],
    anatomy: ['Button with the language name', 'Menu of languages'],
    accessibility: [
      'Each language name is written in its own language and marked with lang, so it is pronounced correctly.',
      'With more than two languages, a menu with radio items: arrows move, Escape closes.',
    ],
    uswds: ['Language selector'],
    classes: ['mds-language', 'mds-language__toggle', 'mds-language__menu', 'mds-language__item'],
    tokens: ['--mds-border-strong', '--mds-primary', '--mds-radius-control'],
    example: `import { LanguageSelector } from 'missiondesign-react'

<LanguageSelector value={lang} onChange={setLang} languages={[
  { lang: 'en', name: 'English', english: 'English' },
  { lang: 'es', name: 'Español', english: 'Spanish' },
]} />`,
    related: ['header'],
  },
  {
    id: 'textarea',
    name: 'Textarea',
    category: 'Forms',
    summary: 'A labelled field for answers longer than a line, styled like the text input and resized only in height.',
    useWhen: [
      'Free-text answers of a sentence or more, such as a description or a comment.',
      'Use a text input for a single line, and a character count when the answer has a firm limit.',
    ],
    anatomy: ['Label', 'Hint', 'Textarea', 'Error message'],
    accessibility: [
      'The label is tied to the field with for and id; hint and error are connected with aria-describedby.',
      'An error sets aria-invalid and is written out in words beside the red border, so it never rests on color alone.',
      'Validate on blur or submit, never on each keystroke.',
    ],
    uswds: ['Textarea'],
    classes: ['mds-field', 'mds-label', 'mds-hint', 'mds-textarea', 'mds-error-text'],
    tokens: ['--mds-border-strong', '--mds-error', '--mds-radius-field', '--mds-focus'],
    example: `<div class="mds-field">
  <label class="mds-label" for="details">What happened?</label>
  <span class="mds-hint" id="details-hint">Include the date and the office.</span>
  <textarea id="details" class="mds-textarea" rows="5" aria-describedby="details-hint"></textarea>
</div>`,
    related: ['text-input', 'character-count'],
  },
  {
    id: 'switch',
    name: 'Switch',
    category: 'Forms',
    summary: 'An on and off control for a setting that takes effect at once, built on a native checkbox.',
    useWhen: [
      'Turning a single setting on or off where the change applies immediately, such as email reminders.',
      'Use a checkbox when the choice is submitted with a form, or for agreeing to terms.',
    ],
    anatomy: ['Track', 'Thumb with a check when on', 'Label', 'Optional hint'],
    accessibility: [
      'A native checkbox with role="switch", so screen readers announce it as a switch that is on or off and Space toggles it.',
      'The whole row is the label and the target is at least 44px tall.',
      'The thumb moves and shows a check when on, so the state never rests on color alone; motion stops under reduced motion.',
    ],
    uswds: [],
    classes: ['mds-switch-field', 'mds-switch', 'mds-switch__input', 'mds-switch__track', 'mds-switch__thumb', 'mds-switch__label', 'mds-switch__hint'],
    tokens: ['--mds-primary', '--mds-on-primary', '--mds-border-strong', '--mds-focus'],
    example: `<label class="mds-switch">
  <input type="checkbox" role="switch" class="mds-switch__input" checked />
  <span class="mds-switch__track" aria-hidden="true"><span class="mds-switch__thumb"></span></span>
  <span class="mds-switch__label">Email reminders</span>
</label>`,
    related: ['checkbox-radio', 'segmented-control'],
  },
  {
    id: 'range-slider',
    name: 'Range slider',
    category: 'Forms',
    summary: 'A native range input with a drawn track and thumb, its current value in an output and the ends of the range beside it.',
    useWhen: [
      'Choosing an approximate value within known limits, such as a search radius.',
      'Use a text input when people need an exact number.',
    ],
    anatomy: ['Label', 'Hint', 'Current value', 'Minimum label', 'Track and thumb', 'Maximum label'],
    accessibility: [
      'The browser gives arrows, Page Up, Page Down, Home and End; aria-valuetext reads the value with its unit.',
      'The output element shows the current value as text, tied to the input with for.',
      'The thumb carries a focus ring in the focus color, drawn in WebKit and Firefox alike.',
    ],
    uswds: ['Range slider'],
    classes: ['mds-range-field', 'mds-range', 'mds-range__value', 'mds-range__row', 'mds-range__end'],
    tokens: ['--mds-primary', '--mds-border-strong', '--mds-raised', '--mds-focus', '--mds-shadow-1'],
    example: `import { RangeSlider } from 'missiondesign-react'

<RangeSlider label="Search radius" min={5} max={100} step={5} defaultValue={25} format={(miles) => \`\${miles} miles\`} />`,
    related: ['text-input', 'segmented-control'],
  },
  {
    id: 'date-range-picker',
    name: 'Date range picker',
    category: 'Forms',
    summary: 'Two date pickers for a start and an end, grouped in a fieldset, where each date limits the other.',
    useWhen: [
      'A period with a first and a last day, such as travel dates or a reporting period.',
      'Use a single date picker for one date.',
    ],
    anatomy: ['Fieldset and legend', 'Hint', 'Start date picker', 'End date picker', 'Limits message', 'Error message'],
    accessibility: [
      'A fieldset and legend name the range; each date has its own visible label and keeps the date picker keyboard behavior.',
      'Once a date is entered, a message names the limit it sets on the other: the end on or after the start, the start on or before the end.',
      'An end before the start is described in an error tied to the fieldset with aria-describedby.',
    ],
    uswds: ['Date range picker'],
    classes: ['mds-date-range', 'mds-date-range__legend', 'mds-date-range__fields', 'mds-date-range__limits', 'mds-date', 'mds-error-text'],
    tokens: ['--mds-primary', '--mds-ink-muted', '--mds-error', '--mds-raised'],
    example: `import { DateRangePicker } from 'missiondesign-react'

<DateRangePicker legend="Travel dates" startLabel="Departure date" endLabel="Return date" onChange={({ start, end }) => console.log(start, end)} />`,
    related: ['date-picker', 'memorable-date', 'time-picker'],
  },
  {
    id: 'tabs',
    name: 'Tabs',
    category: 'Navigation',
    summary: 'A row of tabs that switches between panels of related content, one panel shown at a time.',
    useWhen: [
      'Related views of one thing, such as the overview, documents and history of an application.',
      'Keep labels to one or two words and the set to a single row; use the in-page navigation for long pages people read top to bottom.',
    ],
    anatomy: ['Tab list', 'Tabs', 'Selected tab marker', 'Panel'],
    accessibility: [
      'Follows the WAI-ARIA tabs pattern: a tablist of tabs, each controlling a tabpanel labelled by its tab.',
      'Only the selected tab is in the tab order; Left and Right arrows move and select, Home and End jump to the first and last.',
      'The selected tab is marked by an underline and a heavier weight, not by color alone.',
    ],
    uswds: [],
    classes: ['mds-tabs', 'mds-tabs__list', 'mds-tabs__tab', 'mds-tabs__panel'],
    tokens: ['--mds-primary', '--mds-ink-muted', '--mds-border', '--mds-radius-field'],
    example: `import { Tabs } from 'missiondesign-react'

<Tabs label="Application details" tabs={[
  { id: 'overview', label: 'Overview', content: <p>Your application is in review.</p> },
  { id: 'documents', label: 'Documents', content: <p>Proof of identity, received.</p> },
]} />`,
    related: ['segmented-control', 'accordion', 'in-page-navigation'],
  },
  {
    id: 'in-page-navigation',
    name: 'In-page navigation',
    category: 'Navigation',
    summary: 'An "On this page" list of a page\'s sections that marks the one in view, and stays beside the content on wide screens.',
    useWhen: [
      'Long pages with three or more sections, such as guidance and policy pages.',
      'Build it from the page\'s h2 headings, so the list matches what people see.',
    ],
    anatomy: ['Nav landmark', 'Title', 'Section links', 'Current section marker'],
    accessibility: [
      'A nav landmark labelled by its "On this page" title, holding a list of links to the section headings.',
      'The link for the section in view has aria-current="location" and is marked by a bar and a heavier weight, not by color alone.',
      'Each link is a plain anchor to the section heading, so the list works without script.',
    ],
    uswds: ['In-page navigation'],
    classes: ['mds-in-page-nav', 'mds-in-page-nav__title', 'mds-in-page-nav__list', 'mds-in-page-nav__link'],
    tokens: ['--mds-primary', '--mds-ink-muted', '--mds-border', '--mds-header-height'],
    example: `<nav class="mds-in-page-nav" aria-labelledby="on-this-page">
  <p class="mds-in-page-nav__title" id="on-this-page">On this page</p>
  <ul class="mds-in-page-nav__list">
    <li><a class="mds-in-page-nav__link" href="#eligibility" aria-current="location">Eligibility</a></li>
    <li><a class="mds-in-page-nav__link" href="#documents">Documents you need</a></li>
    <li><a class="mds-in-page-nav__link" href="#apply">How to apply</a></li>
  </ul>
</nav>`,
    related: ['side-navigation', 'tabs', 'prose'],
  },
  {
    id: 'dropdown-menu',
    name: 'Dropdown menu',
    category: 'Actions',
    summary: 'A button that opens a short list of actions, for when there are more than fit beside the content.',
    useWhen: [
      'Secondary actions on a record or a row, such as download, share and print.',
      'Keep the primary action as its own button, and use links in navigation rather than a menu.',
    ],
    anatomy: ['Menu button with chevron', 'Menu', 'Menu items'],
    accessibility: [
      'Follows the WAI-ARIA menu button pattern: the button has aria-haspopup="menu" and aria-expanded, and the menu is labelled by the button.',
      'Enter, Space or Down Arrow opens the menu on the first item; arrows, Home and End move, and typing a letter jumps to the next matching item.',
      'Escape closes the menu and returns focus to the button; Tab and a click outside close it too.',
    ],
    uswds: [],
    classes: ['mds-dropdown', 'mds-dropdown--start', 'mds-dropdown--end', 'mds-dropdown__button', 'mds-dropdown__chevron', 'mds-dropdown__menu', 'mds-dropdown__item'],
    tokens: ['--mds-raised', '--mds-border-strong', '--mds-shadow-1', '--mds-focus', '--mds-radius-field'],
    example: `import { DropdownMenu } from 'missiondesign-react'

<DropdownMenu label="Actions" items={[
  { label: 'Download a copy', onSelect: download },
  { label: 'Share', onSelect: share },
]} />`,
    related: ['button', 'language-selector', 'popover'],
  },
  {
    id: 'link',
    name: 'Link',
    category: 'Navigation',
    summary: 'An underlined link in the link color, with an external variant that names when it opens a new tab.',
    useWhen: [
      'Links inside running text; links in prose pick up the style without the class.',
      'Open a new tab only when leaving would lose work, and use the external variant whenever a link does.',
    ],
    anatomy: ['Link text', 'Underline', 'External icon and "(opens in a new tab)" text'],
    accessibility: [
      'Always underlined, so a link is found by more than its color; the underline thickens on hover.',
      'The link text names its destination on its own, never "click here".',
      'A link with target="_blank" adds visually hidden "(opens in a new tab)" text, and the icon is hidden from screen readers.',
    ],
    uswds: ['Link'],
    classes: ['mds-link', 'mds-link--external', 'mds-visually-hidden'],
    tokens: ['--mds-link', '--mds-focus'],
    example: `<a class="mds-link" href="/eligibility">who can apply</a>

<a class="mds-link mds-link--external" href="https://www.usa.gov" target="_blank" rel="noopener noreferrer">
  USA.gov<span class="mds-visually-hidden"> (opens in a new tab)</span>
  <svg aria-hidden="true">…</svg>
</a>`,
    related: ['prose', 'button', 'icon'],
  },
  {
    id: 'toast',
    name: 'Toast',
    category: 'Feedback',
    summary: 'A brief status message that rises at the bottom of the screen and closes itself after a few seconds.',
    useWhen: [
      'Confirming that an action finished, such as a saved draft or an updated address.',
      'Never for errors or anything that needs action: use an alert or a field error, which stay until the problem is fixed.',
    ],
    anatomy: ['Region at the bottom of the viewport', 'Icon', 'Message', 'Close button'],
    accessibility: [
      'Messages appear in a polite live region (role="status") that is on the page before any message, so each new one is announced without moving focus.',
      'The countdown pauses while the pointer is over a message or focus is inside it, and every message has a close button (WCAG 2.2.1).',
      'Tone is carried by the icon and the wording as well as color.',
    ],
    uswds: [],
    classes: ['mds-toast-region', 'mds-toast-region__list', 'mds-toast', 'mds-toast--success', 'mds-toast__icon', 'mds-toast__message', 'mds-toast__close'],
    tokens: ['--mds-raised', '--mds-success', '--mds-info', '--mds-shadow-2', '--mds-radius-card'],
    example: `import { Toast } from 'missiondesign-react'

<Toast
  toasts={[{ id: 'saved', message: 'Draft saved.', tone: 'success' }]}
  onDismiss={(id) => setToasts((list) => list.filter((toast) => toast.id !== id))}
/>`,
    related: ['alert', 'site-alert', 'spinner'],
  },
  {
    id: 'progress-bar',
    name: 'Progress bar',
    category: 'Feedback',
    summary: 'A bar that fills as work completes, with a label and the percentage done, or a moving bar when the length is unknown.',
    useWhen: [
      'Uploads, imports and other work that takes more than a few seconds.',
      'Use the indeterminate variant only when the amount left cannot be measured.',
      'Use the step indicator for progress through the pages of a form.',
    ],
    anatomy: ['Label', 'Percentage', 'Track', 'Bar'],
    accessibility: [
      'The track has role="progressbar" with aria-valuenow, aria-valuemin and aria-valuemax, and is named by the visible label.',
      'The indeterminate variant leaves out aria-valuenow, so it is announced as busy rather than at a value.',
      'The indeterminate bar stops moving under prefers-reduced-motion and holds still as a striped fill.',
    ],
    uswds: [],
    classes: ['mds-progress', 'mds-progress--indeterminate', 'mds-progress__head', 'mds-progress__label', 'mds-progress__value', 'mds-progress__track', 'mds-progress__bar'],
    tokens: ['--mds-primary', '--mds-surface', '--mds-border-strong', '--mds-ink-muted'],
    example: `<div class="mds-progress">
  <div class="mds-progress__head">
    <span class="mds-progress__label" id="upload-label">Uploading documents</span>
    <span class="mds-progress__value" aria-hidden="true">45%</span>
  </div>
  <div class="mds-progress__track" role="progressbar" aria-labelledby="upload-label"
    aria-valuemin="0" aria-valuemax="100" aria-valuenow="45">
    <span class="mds-progress__bar" style="width: 45%"></span>
  </div>
</div>`,
    related: ['step-indicator', 'spinner', 'file-input'],
  },
  {
    id: 'spinner',
    name: 'Spinner',
    category: 'Feedback',
    summary: 'A turning ring that shows something is loading, in default and small sizes.',
    useWhen: [
      'Short waits of a few seconds, inside a button or beside the content that is loading.',
      'Use a progress bar when the amount of work is known, and a skeleton when the shape of the content is.',
    ],
    anatomy: ['Ring', 'Loading text, visually hidden or visible'],
    accessibility: [
      'The spinner has role="status" and visually hidden "Loading" text, so screen readers hear what the ring shows.',
      'Under prefers-reduced-motion the ring turns slowly.',
    ],
    uswds: [],
    classes: ['mds-spinner', 'mds-spinner--sm', 'mds-visually-hidden'],
    tokens: ['--mds-primary', '--mds-ink'],
    example: `<span class="mds-spinner" role="status">
  <span class="mds-visually-hidden">Loading</span>
</span>`,
    related: ['progress-bar', 'skeleton', 'button'],
  },
  {
    id: 'skeleton',
    name: 'Skeleton',
    category: 'Feedback',
    summary: 'Gray placeholder shapes in the layout of the content that is loading: a media block, a heading and lines of text.',
    useWhen: [
      'Cards, lists and panels whose layout is known before their content arrives.',
      'Match the shapes to the real content, so nothing jumps when it loads.',
    ],
    anatomy: ['Container', 'Media block', 'Heading', 'Lines'],
    accessibility: [
      'The container carries aria-busy="true" until the content arrives; the shapes are aria-hidden.',
      'Visually hidden text says what is loading.',
      'The shimmer stops under prefers-reduced-motion.',
    ],
    uswds: [],
    classes: ['mds-skeleton', 'mds-skeleton__blocks', 'mds-skeleton__media', 'mds-skeleton__heading', 'mds-skeleton__line', 'mds-skeleton__line--short'],
    tokens: ['--mds-surface', '--mds-ink', '--mds-raised', '--mds-radius-field'],
    example: `<div class="mds-skeleton" aria-busy="true">
  <span class="mds-visually-hidden">Loading office details</span>
  <div class="mds-skeleton__blocks" aria-hidden="true">
    <span class="mds-skeleton__media"></span>
    <span class="mds-skeleton__heading"></span>
    <span class="mds-skeleton__line"></span>
    <span class="mds-skeleton__line mds-skeleton__line--short"></span>
  </div>
</div>`,
    related: ['spinner', 'card', 'media-card'],
  },
  {
    id: 'popover',
    name: 'Popover',
    category: 'Actions',
    summary: 'A panel a button opens beside itself, holding a heading, a few sentences and a link.',
    useWhen: [
      'Optional detail that is longer than a tooltip, or that holds a link.',
      'Use a modal for a decision, and a tooltip for a short name or hint.',
    ],
    anatomy: ['Button', 'Panel', 'Heading', 'Body'],
    accessibility: [
      'A disclosure: the button carries aria-expanded and aria-controls, and the panel follows it in the tab order.',
      'The panel is non-modal; Escape closes it and returns focus to the button, and a click outside closes it.',
    ],
    uswds: [],
    classes: ['mds-popover', 'mds-popover--start', 'mds-popover--end', 'mds-popover__panel', 'mds-popover__title', 'mds-popover__body'],
    tokens: ['--mds-raised', '--mds-border-strong', '--mds-shadow-2', '--mds-radius-card'],
    example: `import { Popover } from 'missiondesign-react'

<Popover label="Processing times" title="Processing times">
  <p>Most applications finish in six to eight weeks.</p>
  <p><a class="mds-link" href="/processing-times">See current processing times</a></p>
</Popover>`,
    related: ['tooltip', 'dropdown-menu', 'modal'],
  },
  {
    id: 'list',
    name: 'List',
    category: 'Content',
    summary: 'Bulleted and numbered lists with even spacing, plus an unstyled variant for lists that should not look like lists.',
    useWhen: [
      'Bullets for items in no particular order; numbers when order matters.',
      'Use the unstyled variant for short groups such as hours or contact details.',
      'Use the process list for steps that need a heading and details each.',
    ],
    anatomy: ['List', 'Marker', 'Item', 'Optional nested list'],
    accessibility: [
      'Real ul and ol elements, so screen readers announce the list and its length.',
      'Markers use ink-muted, which meets 4.5:1 on every surface.',
    ],
    uswds: ['List'],
    classes: ['mds-list', 'mds-list--unstyled'],
    tokens: ['--mds-ink-muted'],
    example: `<ul class="mds-list">
  <li>Your current passport</li>
  <li>One passport photo</li>
</ul>
<ol class="mds-list">
  <li>Fill out the renewal form.</li>
  <li>Mail it with your passport and photo.</li>
</ol>`,
    related: ['list-group', 'icon-list', 'process-list'],
  },
  {
    id: 'list-group',
    name: 'List group',
    category: 'Content',
    summary: 'Items or links in one rounded box, divided by rules, with an optional current item and trailing details.',
    useWhen: [
      'A short set of related pages or records people choose between.',
      'Use the side navigation for a section of a site.',
    ],
    anatomy: ['Box', 'Item or link', 'Divider', 'Optional trailing detail', 'Optional current item'],
    accessibility: [
      'A ul, so the number of items is announced; links stay links, plain items stay text.',
      'The current item carries aria-current and is marked by weight and a bar, not color alone.',
      'Link rows are at least 44px tall and show the focus ring inside the box.',
    ],
    uswds: [],
    classes: ['mds-list-group', 'mds-list-group__item', 'mds-list-group__meta'],
    tokens: ['--mds-raised', '--mds-border', '--mds-primary', '--mds-ink-muted', '--mds-radius-card'],
    example: `<ul class="mds-list-group">
  <li><a class="mds-list-group__item" href="/overview">Overview</a></li>
  <li>
    <a class="mds-list-group__item" href="/apply" aria-current="page">
      How to apply <span class="mds-list-group__meta">4 steps</span>
    </a>
  </li>
</ul>`,
    related: ['list', 'side-navigation', 'card'],
  },
  {
    id: 'icon',
    name: 'Icon',
    category: 'Content',
    summary: 'A set of line icons drawn on a 24px grid in the current text color, at any size.',
    useWhen: [
      'Beside a label, to help people scan: download, external link, calendar.',
      'Alone only in an icon button, and then with an accessible name.',
    ],
    anatomy: ['24px viewBox', 'Single stroked path', 'Current text color'],
    accessibility: [
      'Every icon is aria-hidden, so a decorative icon next to its label is silent.',
      'A meaningful icon gets a name from its control (aria-label on an icon button) or from visually hidden text beside it.',
      'Icons follow the text color, so they keep its contrast and switch with the theme.',
    ],
    uswds: ['Icon'],
    classes: ['mds-icon-button', 'mds-visually-hidden'],
    tokens: ['--mds-ink'],
    example: `import { Icon } from 'missiondesign-react'

<Icon name="download" size={20} />
<button className="mds-icon-button" aria-label="Download">
  <Icon name="download" />
</button>`,
    related: ['icon-list', 'button', 'tooltip'],
  },
  {
    id: 'icon-list',
    name: 'Icon list',
    category: 'Content',
    summary: 'A list where each item leads with an icon, such as checkmarks for requirements or symbols for ways to apply.',
    useWhen: [
      'Short lists people scan: what to bring, what is included, ways to get help.',
      'Use one icon throughout for a checklist, or a different icon for each kind of item.',
    ],
    anatomy: ['List', 'Icon', 'Item text', 'Optional title and description'],
    accessibility: [
      'Icons are decorative; the item text carries the meaning, so nothing depends on the icon.',
      'Icons use accent-ink, which meets 4.5:1 on every surface.',
    ],
    uswds: ['Icon list'],
    classes: ['mds-icon-list', 'mds-icon-list__icon'],
    tokens: ['--mds-accent-ink', '--mds-space-3'],
    example: `<ul class="mds-icon-list">
  <li>
    <span class="mds-icon-list__icon"><svg aria-hidden="true">…</svg></span>
    A current passport
  </li>
  <li>
    <span class="mds-icon-list__icon"><svg aria-hidden="true">…</svg></span>
    One passport photo
  </li>
</ul>`,
    related: ['list', 'icon', 'summary-box'],
  },
  {
    id: 'prose',
    name: 'Prose',
    category: 'Content',
    summary: 'Readable measure and spacing for long-form text: headings, paragraphs, lists and links styled from plain HTML.',
    useWhen: [
      'Articles, guidance and policy pages, and any text that comes from a content system as plain HTML.',
      'Wrap the article body once; its elements need no classes.',
    ],
    anatomy: ['Container at text width', 'Headings', 'Paragraphs', 'Lists', 'Links'],
    accessibility: [
      'Lines stay near 70 characters at a 1.65 line height, for comfortable reading.',
      'Links are underlined, so they are not marked by color alone.',
      'Headings keep their levels, so the outline is navigable.',
    ],
    uswds: ['Prose'],
    classes: ['mds-prose'],
    tokens: ['--mds-container-text', '--mds-font-display', '--mds-text-h2', '--mds-text-h3', '--mds-link'],
    example: `<article class="mds-prose">
  <h2>Renew your passport by mail</h2>
  <p>Most adults can renew by mail.</p>
  <ul>
    <li>The renewal form, signed and dated</li>
    <li>Your most recent passport</li>
  </ul>
  <p><a href="/fees">See fees and processing times</a>.</p>
</article>`,
    related: ['list', 'link'],
  },
  {
    id: 'carousel',
    name: 'Carousel',
    category: 'Content',
    summary: 'A sideways row of cards the page scrolls past, moved with Previous and Next buttons, a swipe or a trackpad.',
    useWhen: [
      'A handful of comparable items that do not all need to be seen, such as featured services.',
      'Put anything everyone needs on the page itself, not in a slide.',
    ],
    anatomy: ['Region', 'Previous and Next buttons', 'Scrolling viewport', 'Slides'],
    accessibility: [
      'A region with aria-roledescription="carousel" and a label; each slide is a group labelled "n of m".',
      'It never moves on its own. Slides snap into place, and the buttons scroll instantly under reduced motion.',
      'A button hides at its end, and focus there moves to the other button.',
    ],
    uswds: [],
    classes: ['mds-carousel', 'mds-carousel__viewport', 'mds-carousel__track', 'mds-carousel__slide', 'mds-carousel__button', 'mds-carousel__button--back'],
    tokens: ['--mds-raised', '--mds-border-strong', '--mds-shadow-1', '--mds-space-4'],
    example: `import { Carousel } from 'missiondesign-react'

<Carousel label="Popular services">
  <article className="mds-card mds-card--outline">…</article>
  <article className="mds-card mds-card--outline">…</article>
</Carousel>`,
    related: ['card', 'media-card'],
  },
]

export function findComponent(id: string | undefined) {
  return components.find((component) => component.id === id)
}
